import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import HeaderClientes from "components/_refactor/headers/clients";
import LoadingData from "components/Loading/LoadingData";
import Api from "lib/api/httpRequestHelper";
import BulkSelectionModal from "views/vitrine-b2b/shared/components/bulk-selection-modal";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";

const useStyles = makeStyles((theme) => ({
  ...componentStyles(theme),
  tabRoot: {
    color: themeColorsDesingSystem.gray.dark,
    opacity: 1,
  },
  tabSelected: {
    color: themeColorsDesingSystem.primary.main,
  },
}));

const ENDPOINT = "customerReactivationDigitalApi";
const PAGE_SIZE = 25;

const TEMPLATE_STATUS_COLOR = {
  APPROVED: "primary",
  PENDING: "default",
  REJECTED: "secondary",
};

const TEMPLATE_STATUS_LABEL = {
  APPROVED: "Aprovado",
  PENDING: "Em aprovação",
  REJECTED: "Rejeitado",
};

const TEMPLATE_CATEGORY_LABEL = {
  MARKETING: "Marketing",
  UTILITY: "Utilidade",
  AUTHENTICATION: "Autenticação",
};

const TEMPLATE_LANGUAGE_LABEL = {
  pt_BR: "Português (Brasil)",
  en_US: "Inglês (EUA)",
  es_ES: "Espanhol (Espanha)",
  es_AR: "Espanhol (Argentina)",
};

const CLASSIFICATION_LABEL = {
  interesse: "Interesse em comprar",
  sem_interesse: "Sem interesse",
  opt_out: "Pediu para não ser contatado",
  fora_de_escopo: "Fora do assunto",
  atendimento_humano: "Pediu atendente",
  falha_entrega: "Falha ao entregar mensagem",
};

const RESULT_LABEL = {
  ...CLASSIFICATION_LABEL,
  limite: "Limite de tentativas atingido",
};

const JOURNEY_STATUS_LABEL = {
  pendente_confirmacao: "Aguardando confirmação",
  em_andamento: "Em conversa",
  encerrada: "Encerrada",
  transferida: "Transferida para atendente",
};

function translateLabel(map, value) {
  if (!value) return null;
  return map[value] || value;
}

function getJourneyStatusLabel(journey) {
  return translateLabel(JOURNEY_STATUS_LABEL, journey.Status);
}

const SECONDARY_TEXT_COLOR = themeColorsDesingSystem.gray.dark;

function getLocalDigits(phone) {
  let digits = (phone || "").replace(/\D/g, "");
  if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
  return digits;
}

function isValidWhatsapp(phone) {
  const digits = getLocalDigits(phone);
  return digits.length === 10 || digits.length === 11;
}

function formatWhatsapp(phone) {
  const digits = getLocalDigits(phone);
  if (digits.length === 11) {
    return `+55 (${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+55 (${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone || "—";
}

const EMPTY_CAMPAIGN = {
  nome: "",
  nomeTemplateMeta: "",
  mensagemInicialTemplate: "",
  maxInteracoes: 6,
  maxTentativas: 3,
  intervaloTentativaHoras: 48,
};

const EMPTY_TEMPLATE = {
  nome: "",
  categoria: "MARKETING",
  idioma: "pt_BR",
  corpo: "",
  exemploVariaveis: [],
};

// Conta pelo maior índice {{n}} encontrado no corpo — a Meta exige um exemplo por variável
// usada, na mesma ordem em que aparecem (ver CriarTemplateRequestDto.ExemploVariaveis no
// backend). Antes disso o formulário só tinha um campo fixo "exemploVariavel1" (string),
// que nunca batia com o formato que o backend espera (lista) — template com variável no
// corpo sempre ia pra Meta sem nenhum exemplo e tendia a ser rejeitado.
function contarVariaveisTemplate(corpo) {
  const indices = [...(corpo || "").matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) =>
    Number(m[1]),
  );
  return indices.length === 0 ? 0 : Math.max(...indices);
}

function GerenciamentoReativacaoIA() {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [tab, setTab] = useState(0);

  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [candidateStatusFilter, setCandidateStatusFilter] = useState("ativos");
  const [diasSemCompraFilter, setDiasSemCompraFilter] = useState("");

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState(EMPTY_TEMPLATE);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState(EMPTY_CAMPAIGN);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [startingCampaignId, setStartingCampaignId] = useState(null);
  const [loadingTemplateCorpo, setLoadingTemplateCorpo] = useState(false);

  const [journeys, setJourneys] = useState([]);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [journeyStatusFilter, setJourneyStatusFilter] = useState("");
  const [journeyNomeClienteFilter, setJourneyNomeClienteFilter] = useState("");
  const [journeyDataInicioDeFilter, setJourneyDataInicioDeFilter] = useState("");
  const [journeyDataInicioAteFilter, setJourneyDataInicioAteFilter] = useState("");

  const approvedTemplates = templates.filter((t) => t.Status === "APPROVED");

  const validCandidates = useMemo(
    () => candidates.filter((c) => isValidWhatsapp(c.FoneCelular)),
    [candidates],
  );
  const totalPages = Math.max(1, Math.ceil(candidates.length / PAGE_SIZE));
  const currentPageCandidates = useMemo(
    () =>
      candidates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [candidates, currentPage],
  );
  const validCurrentPageCandidates = currentPageCandidates.filter((c) =>
    isValidWhatsapp(c.FoneCelular),
  );

  const loadCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const params = new URLSearchParams({ pageSize: "1000" });
      params.set("status", candidateStatusFilter);
      if (diasSemCompraFilter !== "") {
        params.set("diasSemCompra", diasSemCompraFilter);
      }
      const { Success, Result } = await Api.get(
        "candidates",
        ENDPOINT,
        `?${params.toString()}`,
        true,
      );
      if (Success) {
        const list = Result?.Lista || [];
        setCandidates(list);
        setCurrentPage(1);
        setSelected(
          new Set(
            list
              .filter((c) => isValidWhatsapp(c.FoneCelular))
              .map((c) => c.IdPessoa),
          ),
        );
      }
    } catch (error) {
      snackbar.showMessage(`Erro ao carregar destinatários - ${error}`, {
        severity: "error",
      });
    } finally {
      setLoadingCandidates(false);
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { Success, Result } = await Api.get(
        "templates",
        ENDPOINT,
        "",
        true,
      );
      if (Success) setTemplates(Result || []);
    } catch (error) {
      snackbar.showMessage(`Erro ao carregar templates - ${error}`, {
        severity: "error",
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadTemplateCorpo = async (metaTemplateId) => {
    if (!metaTemplateId) {
      setNewCampaign((current) => ({ ...current, mensagemInicialTemplate: "" }));
      return;
    }
    setLoadingTemplateCorpo(true);
    setNewCampaign((current) => ({ ...current, mensagemInicialTemplate: "" }));
    try {
      const { Success, Result } = await Api.get(
        `templates/${metaTemplateId}/body`,
        ENDPOINT,
        "",
        true,
      );
      if (Success) {
        setNewCampaign((current) => ({
          ...current,
          mensagemInicialTemplate: Result?.Corpo || "",
        }));
      }
    } catch (error) {
      snackbar.showMessage(`Erro ao buscar conteúdo do template - ${error}`, {
        severity: "error",
      });
    } finally {
      setLoadingTemplateCorpo(false);
    }
  };

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const { Success, Result } = await Api.get(
        "campaigns",
        ENDPOINT,
        "",
        true,
      );
      if (Success) setCampaigns(Result || []);
    } catch (error) {
      snackbar.showMessage(`Erro ao carregar campanhas - ${error}`, {
        severity: "error",
      });
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadJourneys = async () => {
    setLoadingJourneys(true);
    try {
      const params = new URLSearchParams();
      if (journeyStatusFilter) params.set("status", journeyStatusFilter);
      if (journeyNomeClienteFilter.trim()) {
        params.set("nomeCliente", journeyNomeClienteFilter.trim());
      }
      if (journeyDataInicioDeFilter) {
        params.set("dataInicioDe", journeyDataInicioDeFilter);
      }
      if (journeyDataInicioAteFilter) {
        params.set("dataInicioAte", journeyDataInicioAteFilter);
      }
      const query = params.toString();
      const { Success, Result } = await Api.get(
        "journeys",
        ENDPOINT,
        query ? `?${query}` : "",
        true,
      );
      if (Success) setJourneys(Result || []);
    } catch (error) {
      snackbar.showMessage(`Erro ao carregar jornadas - ${error}`, {
        severity: "error",
      });
    } finally {
      setLoadingJourneys(false);
    }
  };

  useEffect(() => {
    loadCandidates();
    loadTemplates();
    loadCampaigns();
    loadJourneys();
  }, []);

  const toggleSelection = (personId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  };

  const selectPage = () => {
    setSelected(
      (current) =>
        new Set([
          ...current,
          ...validCurrentPageCandidates.map((c) => c.IdPessoa),
        ]),
    );
  };

  const deselectPage = () => {
    setSelected((current) => {
      const next = new Set(current);
      validCurrentPageCandidates.forEach((c) => next.delete(c.IdPessoa));
      return next;
    });
  };

  const selectAll = () =>
    setSelected(new Set(validCandidates.map((c) => c.IdPessoa)));

  const deselectAll = () => setSelected(new Set());

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      // Corta (ou preenche) a lista de exemplos pro tamanho exigido pelo corpo atual — evita
      // mandar exemplos "sobrando" de uma edição anterior do corpo com mais variáveis.
      const variaveisEsperadas = contarVariaveisTemplate(newTemplate.corpo);
      const exemploVariaveis = Array.from(
        { length: variaveisEsperadas },
        (_, index) => newTemplate.exemploVariaveis[index] || "",
      );
      const { Success } = await Api.post(
        "templates",
        ENDPOINT,
        { ...newTemplate, exemploVariaveis },
        true,
      );
      if (Success) {
        snackbar.showMessage("Template enviado para aprovação da Meta.", {
          severity: "success",
        });
        setTemplateDialogOpen(false);
        setNewTemplate(EMPTY_TEMPLATE);
        loadTemplates();
      }
    } catch (error) {
      snackbar.showMessage(`Erro ao criar template - ${error}`, {
        severity: "error",
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const saveCampaign = async () => {
    setSavingCampaign(true);
    try {
      const { Success } = await Api.post(
        "campaigns",
        ENDPOINT,
        newCampaign,
        true,
      );
      if (Success) {
        snackbar.showMessage("Campanha criada.", { severity: "success" });
        setCampaignDialogOpen(false);
        setNewCampaign(EMPTY_CAMPAIGN);
        loadCampaigns();
      }
    } catch (error) {
      snackbar.showMessage(`Erro ao criar campanha - ${error}`, {
        severity: "error",
      });
    } finally {
      setSavingCampaign(false);
    }
  };

  const startCampaign = async (campaignId) => {
    const personIds = Array.from(selected);
    if (personIds.length === 0) {
      snackbar.showMessage(
        "Selecione ao menos um destinatário na aba Destinatários.",
        {
          severity: "error",
        },
      );
      return;
    }

    setStartingCampaignId(campaignId);
    try {
      const { Success, Result } = await Api.post(
        `campaigns/${campaignId}/start`,
        ENDPOINT,
        { pessoaIds: personIds },
        true,
      );
      if (Success) {
        snackbar.showMessage(
          `Campanha iniciada — ${Result?.Iniciadas || 0} jornada(s) disparada(s).` +
            (Result?.Falhas?.length
              ? ` ${Result.Falhas.length} falha(s).`
              : ""),
          { severity: Result?.Falhas?.length ? "warning" : "success" },
        );
        loadJourneys();
        setTab(2);
      }
    } catch (error) {
      snackbar.showMessage(`Erro ao iniciar campanha - ${error}`, {
        severity: "error",
      });
    } finally {
      setStartingCampaignId(null);
    }
  };

  const openJourneyConversation = async (journey) => {
    setSelectedJourney(journey);
    try {
      const { Success, Result } = await Api.get(
        `journeys/${journey.IdIaJornada}`,
        ENDPOINT,
        "",
        true,
      );
      if (Success) setConversationMessages(Result || []);
    } catch (error) {
      snackbar.showMessage(`Erro ao carregar conversa - ${error}`, {
        severity: "error",
      });
    }
  };

  return (
    <>
      <HeaderClientes
        section="Agente de Reativação B2B"
        subsection="Gerenciamento"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <Tabs
                value={tab}
                onChange={(_, value) => setTab(value)}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                classes={{ root: classes.tabRoot }}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: themeColorsDesingSystem.primary.main,
                  },
                }}
              >
                <Tab
                  classes={{
                    root: classes.tabRoot,
                    selected: classes.tabSelected,
                  }}
                  label={`Destinatários (${selected.size}/${validCandidates.length})`}
                />
                <Tab
                  classes={{
                    root: classes.tabRoot,
                    selected: classes.tabSelected,
                  }}
                  label="Campanhas & Templates"
                />
                <Tab
                  classes={{
                    root: classes.tabRoot,
                    selected: classes.tabSelected,
                  }}
                  label={`Jornadas (${journeys.length})`}
                />
              </Tabs>
            </Card>

            {tab === 0 && (
              <Card
                classes={{ root: classes.cardRoot }}
                style={{ marginTop: "1rem" }}
              >
                <CardHeader
                  title="Destinatários elegíveis"
                  subheader="Lojistas inativos com WhatsApp cadastrado — todos vêm marcados por padrão. Número inválido não pode ser selecionado."
                  subheaderTypographyProps={{
                    style: { color: SECONDARY_TEXT_COLOR },
                  }}
                  action={
                    <Button
                      variant="outlined"
                      onClick={() => setSelectionModalOpen(true)}
                      disabled={candidates.length === 0}
                    >
                      Selecionar
                    </Button>
                  }
                />
                <CardContent>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: "1rem" }}
                    alignItems="flex-end"
                    marginBottom="1rem"
                  >
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      select
                      label="Situação"
                      style={{ minWidth: 160 }}
                      value={candidateStatusFilter}
                      onChange={(e) => setCandidateStatusFilter(e.target.value)}
                    >
                      <MenuItem value="ativos">Ativos</MenuItem>
                      <MenuItem value="inativos">Inativos</MenuItem>
                      <MenuItem value="todos">Todos</MenuItem>
                    </TextField>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      label="Dias sem comprar (mínimo)"
                      type="number"
                      style={{ minWidth: 200 }}
                      placeholder="180"
                      value={diasSemCompraFilter}
                      onChange={(e) => setDiasSemCompraFilter(e.target.value)}
                    />
                    <Button variant="contained" onClick={loadCandidates}>
                      Filtrar
                    </Button>
                  </Box>
                  <LoadingData
                    searching={loadingCandidates}
                    classes={classes.root}
                    label="Buscando destinatários..."
                  />
                  {!loadingCandidates && (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Nome</TableCell>
                          <TableCell>WhatsApp</TableCell>
                          <TableCell>Situação</TableCell>
                          <TableCell>Última compra</TableCell>
                          <TableCell>Dias sem comprar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentPageCandidates.map((candidate) => {
                          const valid = isValidWhatsapp(candidate.FoneCelular);
                          const checkbox = (
                            <Checkbox
                              checked={selected.has(candidate.IdPessoa)}
                              disabled={!valid}
                              onChange={() =>
                                toggleSelection(candidate.IdPessoa)
                              }
                            />
                          );
                          return (
                            <TableRow key={candidate.IdPessoa}>
                              <TableCell padding="checkbox">
                                {valid ? (
                                  checkbox
                                ) : (
                                  <Tooltip title="Número precisa ser atualizado no cadastro para ser selecionado">
                                    <span>{checkbox}</span>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell>{candidate.Nome}</TableCell>
                              <TableCell>
                                {valid ? (
                                  formatWhatsapp(candidate.FoneCelular)
                                ) : (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 8 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      style={{ color: "#B91C1C" }}
                                    >
                                      {candidate.FoneCelular || "—"}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label="Número inválido — atualizar cadastro"
                                      style={{
                                        backgroundColor: "#FEE2E2",
                                        color: "#B91C1C",
                                      }}
                                    />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>{candidate.Situacao || "—"}</TableCell>
                              <TableCell>
                                {candidate.DataUltimaCompra
                                  ? new Date(
                                      candidate.DataUltimaCompra,
                                    ).toLocaleDateString("pt-BR")
                                  : "Nunca comprou"}
                              </TableCell>
                              <TableCell>
                                {candidate.DiasSemComprar ?? "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {candidates.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              Nenhum destinatário elegível encontrado.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                  {totalPages > 1 && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      style={{ padding: "1rem 0" }}
                    >
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {tab === 1 && (
              <>
                <Card
                  classes={{ root: classes.cardRoot }}
                  style={{ marginTop: "1rem" }}
                >
                  <CardHeader
                    title="Templates aprovados pela Meta"
                    subheader="Só templates aprovados podem disparar o primeiro contato de uma campanha."
                    subheaderTypographyProps={{
                      style: { color: SECONDARY_TEXT_COLOR },
                    }}
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setTemplateDialogOpen(true)}
                      >
                        Novo template
                      </Button>
                    }
                  />
                  <CardContent>
                    <LoadingData
                      searching={loadingTemplates}
                      classes={classes.root}
                      label="Buscando templates..."
                    />
                    {!loadingTemplates && (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Categoria</TableCell>
                            <TableCell>Idioma</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {templates.map((template) => (
                            <TableRow key={template.Nome}>
                              <TableCell>{template.Nome}</TableCell>
                              <TableCell>
                                {translateLabel(
                                  TEMPLATE_CATEGORY_LABEL,
                                  template.Categoria,
                                )}
                              </TableCell>
                              <TableCell>
                                {translateLabel(
                                  TEMPLATE_LANGUAGE_LABEL,
                                  template.Idioma,
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={translateLabel(
                                    TEMPLATE_STATUS_LABEL,
                                    template.Status,
                                  )}
                                  color={
                                    TEMPLATE_STATUS_COLOR[template.Status] ||
                                    "default"
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {templates.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4}>
                                Nenhum template cadastrado.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                <Card
                  classes={{ root: classes.cardRoot }}
                  style={{ marginTop: "1rem" }}
                >
                  <CardHeader
                    title="Campanhas"
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setCampaignDialogOpen(true)}
                        disabled={approvedTemplates.length === 0}
                      >
                        Nova campanha
                      </Button>
                    }
                  />
                  <CardContent>
                    {approvedTemplates.length === 0 && (
                      <Typography
                        variant="body2"
                        style={{
                          color: SECONDARY_TEXT_COLOR,
                          marginBottom: "1rem",
                        }}
                      >
                        Nenhum template aprovado ainda — crie um template acima
                        e aguarde a aprovação da Meta antes de criar uma
                        campanha.
                      </Typography>
                    )}
                    <LoadingData
                      searching={loadingCampaigns}
                      classes={classes.root}
                      label="Buscando campanhas..."
                    />
                    {!loadingCampaigns && (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Template</TableCell>
                            <TableCell>Mensagem inicial</TableCell>
                            <TableCell>Ativa</TableCell>
                            <TableCell align="right">Ação</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {campaigns.map((campaign) => (
                            <TableRow key={campaign.IdIaCampanha}>
                              <TableCell>{campaign.Nome}</TableCell>
                              <TableCell>
                                {campaign.NomeTemplateMeta || "—"}
                              </TableCell>
                              <TableCell>
                                <Tooltip
                                  title={campaign.MensagemInicialTemplate || ""}
                                >
                                  <Typography
                                    variant="body2"
                                    style={{
                                      maxWidth: 320,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {campaign.MensagemInicialTemplate || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                {campaign.Ativo ? "Sim" : "Não"}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    startCampaign(campaign.IdIaCampanha)
                                  }
                                  disabled={
                                    startingCampaignId === campaign.IdIaCampanha
                                  }
                                >
                                  {startingCampaignId === campaign.IdIaCampanha
                                    ? "Iniciando..."
                                    : `Iniciar campanha (${selected.size})`}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          {campaigns.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                Nenhuma campanha criada.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {tab === 2 && (
              <Card
                classes={{ root: classes.cardRoot }}
                style={{ marginTop: "1rem" }}
              >
                <CardHeader
                  title="Jornadas"
                  subheader="Clique numa linha para ver a conversa."
                  subheaderTypographyProps={{
                    style: { color: SECONDARY_TEXT_COLOR },
                  }}
                  action={
                    <Button variant="contained" onClick={loadJourneys}>
                      Atualizar
                    </Button>
                  }
                />
                <CardContent>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: "1rem" }}
                    alignItems="flex-end"
                    marginBottom="1rem"
                  >
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      select
                      label="Status"
                      SelectProps={{ displayEmpty: true }}
                      style={{ minWidth: 180 }}
                      value={journeyStatusFilter}
                      onChange={(e) => setJourneyStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {Object.entries(JOURNEY_STATUS_LABEL).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      label="Cliente"
                      style={{ minWidth: 200 }}
                      value={journeyNomeClienteFilter}
                      onChange={(e) => setJourneyNomeClienteFilter(e.target.value)}
                    />
                    <TextField
                      label="Início de"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={journeyDataInicioDeFilter}
                      onChange={(e) =>
                        setJourneyDataInicioDeFilter(e.target.value)
                      }
                    />
                    <TextField
                      label="Início até"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={journeyDataInicioAteFilter}
                      onChange={(e) =>
                        setJourneyDataInicioAteFilter(e.target.value)
                      }
                    />
                    <Button variant="contained" onClick={loadJourneys}>
                      Filtrar
                    </Button>
                  </Box>
                  <LoadingData
                    searching={loadingJourneys}
                    classes={classes.root}
                    label="Buscando jornadas..."
                  />
                  {!loadingJourneys && (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pessoa</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Classificação</TableCell>
                          <TableCell>Resultado</TableCell>
                          <TableCell>Interações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {journeys.map((journey) => (
                          <TableRow
                            key={journey.IdIaJornada}
                            hover
                            style={{ cursor: "pointer" }}
                            onClick={() => openJourneyConversation(journey)}
                          >
                            <TableCell>{journey.NomePessoa}</TableCell>
                            <TableCell>
                              {getJourneyStatusLabel(journey)}
                            </TableCell>
                            <TableCell>
                              {translateLabel(
                                CLASSIFICATION_LABEL,
                                journey.Classificacao,
                              ) || "—"}
                            </TableCell>
                            <TableCell>
                              {translateLabel(
                                RESULT_LABEL,
                                journey.Resultado,
                              ) || "—"}
                            </TableCell>
                            <TableCell>{journey.NumInteracoes}</TableCell>
                          </TableRow>
                        ))}
                        {journeys.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              Nenhuma jornada iniciada ainda.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        <Dialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Novo template</DialogTitle>
          <DialogContent>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Nome (minúsculo, sem espaço)"
              fullWidth
              margin="normal"
              value={newTemplate.nome}
              onChange={(e) =>
                setNewTemplate({ ...newTemplate, nome: e.target.value })
              }
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              select
              label="Categoria"
              fullWidth
              margin="normal"
              value={newTemplate.categoria}
              onChange={(e) =>
                setNewTemplate({ ...newTemplate, categoria: e.target.value })
              }
            >
              <MenuItem value="MARKETING">
                {TEMPLATE_CATEGORY_LABEL.MARKETING}
              </MenuItem>
              <MenuItem value="UTILITY">
                {TEMPLATE_CATEGORY_LABEL.UTILITY}
              </MenuItem>
              <MenuItem value="AUTHENTICATION">
                {TEMPLATE_CATEGORY_LABEL.AUTHENTICATION}
              </MenuItem>
            </TextField>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Idioma"
              fullWidth
              margin="normal"
              value={newTemplate.idioma}
              onChange={(e) =>
                setNewTemplate({ ...newTemplate, idioma: e.target.value })
              }
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Corpo (use {{1}}, {{2}}... para variáveis)"
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              value={newTemplate.corpo}
              onChange={(e) =>
                setNewTemplate({ ...newTemplate, corpo: e.target.value })
              }
            />
            {newTemplate.categoria === "MARKETING" && (
              <Typography
                variant="body2"
                style={{ color: SECONDARY_TEXT_COLOR, marginTop: "0.25rem" }}
              >
                Templates de categoria Marketing geram custo por mensagem
                enviada, cobrado pela Meta por categoria e país — não há um
                valor fixo por template.
              </Typography>
            )}
            {Array.from({
              length: contarVariaveisTemplate(newTemplate.corpo),
            }).map((_, index) => (
              <TextField
                InputLabelProps={{ shrink: true }}
                key={index}
                label={`Exemplo de valor para {{${index + 1}}}`}
                fullWidth
                margin="normal"
                value={newTemplate.exemploVariaveis[index] || ""}
                onChange={(e) => {
                  const exemplos = [...newTemplate.exemploVariaveis];
                  exemplos[index] = e.target.value;
                  setNewTemplate({ ...newTemplate, exemploVariaveis: exemplos });
                }}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={saveTemplate}
              disabled={
                savingTemplate || !newTemplate.nome || !newTemplate.corpo
              }
            >
              {savingTemplate ? "Enviando..." : "Enviar para aprovação"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={campaignDialogOpen}
          onClose={() => setCampaignDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Nova campanha</DialogTitle>
          <DialogContent>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Nome da campanha"
              fullWidth
              margin="normal"
              value={newCampaign.nome}
              onChange={(e) =>
                setNewCampaign({ ...newCampaign, nome: e.target.value })
              }
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              select
              label="Template aprovado"
              fullWidth
              margin="normal"
              value={newCampaign.nomeTemplateMeta}
              onChange={(e) => {
                const nomeSelecionado = e.target.value;
                const templateSelecionado = approvedTemplates.find(
                  (t) => t.Nome === nomeSelecionado,
                );
                setNewCampaign({
                  ...newCampaign,
                  nomeTemplateMeta: nomeSelecionado,
                });
                loadTemplateCorpo(templateSelecionado?.MetaTemplateId);
              }}
            >
              {approvedTemplates.map((template) => (
                <MenuItem key={template.Nome} value={template.Nome}>
                  {template.Nome}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              InputLabelProps={{ shrink: true }}
              label="Mensagem inicial"
              helperText="Conteúdo real do template aprovado na Meta — não editável aqui."
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              InputProps={{ readOnly: true }}
              value={
                loadingTemplateCorpo
                  ? "Buscando conteúdo do template..."
                  : newCampaign.mensagemInicialTemplate
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Máx. interações"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={newCampaign.maxInteracoes}
                  onChange={(e) =>
                    setNewCampaign({
                      ...newCampaign,
                      maxInteracoes: Number(e.target.value),
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Máx. tentativas"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={newCampaign.maxTentativas}
                  onChange={(e) =>
                    setNewCampaign({
                      ...newCampaign,
                      maxTentativas: Number(e.target.value),
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Intervalo (horas)"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={newCampaign.intervaloTentativaHoras}
                  onChange={(e) =>
                    setNewCampaign({
                      ...newCampaign,
                      intervaloTentativaHoras: Number(e.target.value),
                    })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCampaignDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={saveCampaign}
              disabled={
                savingCampaign ||
                loadingTemplateCorpo ||
                !newCampaign.nome ||
                !newCampaign.nomeTemplateMeta
              }
            >
              {savingCampaign ? "Salvando..." : "Criar campanha"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(selectedJourney)}
          onClose={() => {
            setSelectedJourney(null);
            setConversationMessages([]);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Conversa — {selectedJourney?.NomePessoa}</DialogTitle>
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              style={{ gap: "0.5rem" }}
            >
              {conversationMessages.map((message, index) => (
                <Box
                  key={index}
                  alignSelf={
                    message.Direcao === "enviada" ? "flex-end" : "flex-start"
                  }
                  bgcolor={
                    message.Direcao === "enviada" ? "#DCF8C6" : "#F1F0F0"
                  }
                  borderRadius="8px"
                  padding="0.5rem 0.75rem"
                  maxWidth="80%"
                >
                  <Typography variant="body2">{message.Conteudo}</Typography>
                  <Typography
                    variant="caption"
                    style={{ color: SECONDARY_TEXT_COLOR }}
                  >
                    {new Date(message.DataHora).toLocaleString("pt-BR")}
                  </Typography>
                </Box>
              ))}
              {conversationMessages.length === 0 && (
                <Typography
                  variant="body2"
                  style={{ color: SECONDARY_TEXT_COLOR }}
                >
                  Sem mensagens ainda.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelectedJourney(null);
                setConversationMessages([]);
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <BulkSelectionModal
          open={selectionModalOpen}
          onClose={() => setSelectionModalOpen(false)}
          title="Selecionar destinatários"
          entityLabel="destinatários"
          pageCount={validCurrentPageCandidates.length}
          pageSelectedCount={
            validCurrentPageCandidates.filter((c) => selected.has(c.IdPessoa))
              .length
          }
          totalCount={validCandidates.length}
          totalSelectedCount={selected.size}
          onSelectPage={selectPage}
          onSelectAll={selectAll}
          onDeselectPage={deselectPage}
          onDeselectAll={deselectAll}
        />
      </Container>
    </>
  );
}

export { GerenciamentoReativacaoIA };
